import { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Translate } from 'react-redux-i18n'
import { UseI18n } from '../../../../common/hooks/useI18n'
import { Element } from '../../../../common/html/element'
import { Icon } from '../../../../common/html/icon'
import { removeAccents } from '../../../../helpers/common'
import { COMPANY_TYPES } from '../../../../utils/common'
import { CRITERIA_GROUP, INDICATOR_GROUP } from '../../common/constant'
import { CustomDropdown } from '../../common/customDropdown'
import { getCurrentTime } from '../../common/helper'
import { Input } from '../../common/input'
import {
  selectBasicInformation,
  selectFinancials,
} from '../../common/store/slice'
import { changeCriteria, selectCriteria } from '../store/slice'
import classes from './index.module.scss'

export const SearchCriteria = () => {
  const dispatch = useDispatch()

  const basicInformation = useSelector(selectBasicInformation)
  const financials = useSelector(selectFinancials)
  const selectedIndicatorIdsStr = useSelector(
    selectCriteria([], (data) => {
      const selectedBasicInformationIds = Object.values(
        data[CRITERIA_GROUP.BASIC_INFORMATION],
      )
        .filter(Boolean)
        .map((item) => item.screeningIndicatorId)
      const selectedFinancialIds = Object.values(
        data[CRITERIA_GROUP.FINANCIALS],
      )
        .filter(Boolean)
        .map((item) => item.screeningIndicatorId)

      return JSON.stringify([
        ...selectedBasicInformationIds,
        ...selectedFinancialIds,
      ])
    }),
  )
  const selectedIndicatorIds = JSON.parse(selectedIndicatorIdsStr)
  const placeholderText = UseI18n('screening.SEARCH_CRITERIA_NAMWE')

  const [keyword, setKeyword] = useState('')

  const data = useMemo(() => {
    return [
      ...basicInformation,
      ...Object.values(financials).reduce(
        (prev, curr) => [
          ...prev,
          ...Object.values(curr).reduce((a, b) => [...a, ...b], []),
        ],
        [],
      ),
    ]
  }, [basicInformation, financials])

  const searchData = useMemo(() => {
    return data
      .filter(
        (item) => !selectedIndicatorIds.includes(item.screeningIndicatorId),
      )
      .filter((item) => {
        const formatItemName = removeAccents(
          item.screeningIndicatorName.toLowerCase(),
        )
        const formatKeyword = removeAccents(keyword.trim().toLowerCase())
        return formatItemName.includes(formatKeyword)
      })
  }, [data, selectedIndicatorIds, keyword])

  const onToggleShowDropdown = (isShow) => {
    if (!isShow) {
      setKeyword('')
    }
  }

  const getGroupName = (code) => {
    switch (code) {
      case COMPANY_TYPES.CORPORATE:
        return 'screening.CORPORATE'
      case COMPANY_TYPES.BANK:
        return 'screening.BANK'
      case COMPANY_TYPES.SECURITIES:
        return 'screening.SECURITIES'
      default:
        return 'screening.BASIC_INFORMATION'
    }
  }

  const renderElement = () => {
    return (
      <Input
        noControl
        value={keyword}
        onChange={(e) => setKeyword(e.target.value)}
        placeholder={placeholderText}
        inputStyle={{ fontSize: 14 }}
        wrapperStyle={{ padding: '5px 12px', backgroundColor: '#FFFFFF' }}
        prefix={
          <Icon className="icon-search pr1" style={{ color: '#9C9C9C' }} />
        }
      />
    )
  }

  const handleChoose = (item) => {
    const group =
      item.indicatorGroup === INDICATOR_GROUP.BASIC_INFORMATION
        ? CRITERIA_GROUP.BASIC_INFORMATION
        : CRITERIA_GROUP.FINANCIALS

    dispatch(
      changeCriteria({
        path: [group, item.screeningIndicatorId],
        data: {
          screeningIndicatorId: item.screeningIndicatorId,
          indicatorType: item.indicatorType,
          createdAt: getCurrentTime(),
        },
      }),
    )
  }

  const renderDropdown = ({ hideDropdown }) => {
    if (!searchData.length) {
      return (
        <Element className="wh100 dFlex justifyContentCenter alignItemsCenter">
          <Translate value="screening.NO_DATA_SEARCH" />
        </Element>
      )
    }

    return searchData.map((item, index) => (
      <DropdownItem
        key={index}
        item={item}
        index={index}
        hideDropdown={hideDropdown}
        handleChoose={handleChoose}
        getGroupName={getGroupName}
      />
    ))
  }

  return (
    <Element className={classes.inputSearch}>
      <CustomDropdown
        renderElement={renderElement}
        renderDropdown={renderDropdown}
        onToggleShowDropdown={onToggleShowDropdown}
        dropdownItemHeight={36}
        maxDropdownItem={7}
        dropdownStyle={{ padding: 0 }}
      />
    </Element>
  )
}

const DropdownItem = ({ item, hideDropdown, handleChoose, getGroupName }) => {
  const [bgColor, setBgColor] = useState('transparent')

  const onMouseEnter = () => {
    setBgColor('#EDF5FE')
  }

  const onMouseLeave = () => {
    setBgColor('transparent')
  }

  return (
    <Element
      onClick={() => {
        hideDropdown()
        handleChoose(item)
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={classes.selectItem}
      style={{ backgroundColor: bgColor }}
    >
      <Element className="fw600" style={{ color: '#3A414B' }}>
        {item.screeningIndicatorName}
      </Element>
      <Element className="fstItalic" style={{ color: '#9C9C9C' }}>
        <Translate value={getGroupName(item.comTypeCode)} />
      </Element>
    </Element>
  )
}
